/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import { platformActions } from '../../slice';
import selectPlatform from '../../selectors';

const TriggerStatus = ({ status }) => (
  <Box aria-live="polite">
    {status.error && <Alert severity="error">{status.error}</Alert>}
    {status.accepted && (
      <Alert severity="success">
        Processing requested. Work continues in the background; this does not confirm completion.
      </Alert>
    )}
  </Box>
);

const PlatformAdminSettingsView = () => {
  const dispatch = useDispatch();
  const organizationId = useSelector(selectOrganizationId);
  const { semantic, nonSemantic } = useSelector(selectPlatform);
  const [jobRunId, setJobRunId] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const validJobRunId = /^\d+$/.test(jobRunId.trim());

  const submitNonSemantic = (event) => {
    event.preventDefault();
    setSubmitted(true);
    if (!organizationId || !validJobRunId || nonSemantic.loading) return;
    dispatch(platformActions.triggerNonSemantic({ organizationId, jobRunId: jobRunId.trim() }));
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h3" gutterBottom>
          Platform Admin Settings
        </Typography>
        <Typography color="text.secondary">
          Trigger analytics processing for your organization using available evaluated results.
          Processing may generate notifications for subscribed users.
        </Typography>
      </Box>
      {!organizationId && (
        <Alert severity="warning">
          Your organization could not be found. Sign in again to continue.
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <Card sx={{ display: 'flex', width: '100%' }}>
            <CardContent sx={{ display: 'flex', flex: 1 }}>
              <Stack spacing={3} sx={{ flex: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" gutterBottom>
                    Semantic Analytics
                  </Typography>
                  <Typography color="text.secondary">
                    Process pending semantic analytics results and start subscribed user alert
                    processing.
                  </Typography>
                </Box>
                <TriggerStatus status={semantic} />
                <Button
                  variant="contained"
                  disabled={!organizationId || semantic.loading}
                  startIcon={
                    semantic.loading ? <CircularProgress size={18} color="inherit" /> : null
                  }
                  onClick={() => dispatch(platformActions.triggerSemantic({ organizationId }))}
                >
                  {semantic.loading ? 'Requesting…' : 'Trigger Semantic Analytics'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <Card sx={{ display: 'flex', width: '100%' }}>
            <CardContent sx={{ display: 'flex', flex: 1 }}>
              <Stack
                component="form"
                spacing={3}
                sx={{ flex: 1 }}
                onSubmit={submitNonSemantic}
                noValidate
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" gutterBottom>
                    Non-Semantic Analytics
                  </Typography>
                  <Typography color="text.secondary">
                    Import evaluated results for a Databricks job run and start subscribed user
                    alert processing.
                  </Typography>
                </Box>
                <TextField
                  label="Job Run ID"
                  size="small"
                  required
                  fullWidth
                  value={jobRunId}
                  disabled={nonSemantic.loading}
                  onChange={(event) => setJobRunId(event.target.value)}
                  inputProps={{ inputMode: 'numeric' }}
                  error={submitted && !validJobRunId}
                  helperText={
                    submitted && !validJobRunId
                      ? 'Enter a Job Run ID containing digits only.'
                      : 'Enter the Databricks run ID for the results to process.'
                  }
                />
                <TriggerStatus status={nonSemantic} />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!organizationId || nonSemantic.loading}
                  startIcon={
                    nonSemantic.loading ? <CircularProgress size={18} color="inherit" /> : null
                  }
                >
                  {nonSemantic.loading ? 'Requesting…' : 'Trigger Non-Semantic Analytics'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default PlatformAdminSettingsView;
